import React from 'react';
import { View, StyleSheet, Text } from 'react-native';
import { Href, Link } from 'expo-router';
import Animated, { FadeIn } from 'react-native-reanimated';
import { Image } from 'expo-image';

type DataType = {
  title: string;
  description: string;
  value: string;
  href: Href;
  coverImage: string;
};

type ArticleListProps = {
  posts: DataType[];
  truncated?: boolean;
  isLoading?: boolean;
};

export function ArticleList({ posts, truncated, isLoading }: ArticleListProps) {
  if (isLoading) {
    return (
      <View style={styles.container} className="mt-12 sm:mt-24 px-6">
        <View className="w-full max-w-[1200px] mx-auto">
          <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <Animated.View key={index} entering={FadeIn.delay(index * 50)}>
                <View style={[styles.card, { opacity: 0.5 }]}>
                  <View
                    style={styles.imageContainer}
                    className="bg-white/[0.03]"
                  />
                  <View style={styles.cardContent}>
                    <View style={styles.textContainer}>
                      <View className="h-4 w-3/4 bg-white/[0.03] rounded" />
                      <View className="h-3 w-1/2 bg-white/[0.03] rounded" />
                    </View>
                    <View className="h-2.5 w-1/4 bg-white/[0.03] rounded" />
                  </View>
                </View>
              </Animated.View>
            ))}
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} className="mt-12 sm:mt-24 px-6">
      <View className="w-full max-w-[1200px] mx-auto">
        <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.slice(0, truncated ? 8 : undefined).map((item, index) => (
            <Animated.View key={item.href} entering={FadeIn.delay(index * 50)}>
              <LineItem {...item} />
            </Animated.View>
          ))}
        </View>
      </View>
    </View>
  );
}

const LineItem = React.memo(function LineItem({
  title,
  description,
  value,
  href,
  coverImage,
}: DataType) {
  return (
    <Link
      href={href}
      className="group/card cursor-pointer transition-all duration-200 active:scale-[0.99] active:opacity-95 hover:bg-white/[0.04] hover:-translate-y-[2px] flex flex-col"
      style={styles.card}
    >
      <View
        style={styles.imageContainer}
        className="group-hover/card:opacity-95"
      >
        <Image
          source={
            typeof coverImage === 'string' ? { uri: coverImage } : coverImage
          }
          style={styles.image}
          contentFit="cover"
        />
      </View>

      <View style={styles.cardContent}>
        <View style={styles.textContainer}>
          <Text
            numberOfLines={2}
            style={styles.title}
            className="group-hover/card:text-white/95"
          >
            {title}
          </Text>
          <Text
            numberOfLines={1}
            style={styles.description}
            className="group-hover/card:text-white/80"
          >
            {description}
          </Text>
        </View>
        <Text style={styles.date} className="group-hover/card:text-white/80">
          {value}
        </Text>
      </View>
    </Link>
  );
});

const styles = StyleSheet.create({
  container: {
    width: '100%',
    alignItems: 'center',
  } as const,
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
    height: '100%',
  } as const,
  imageContainer: {
    width: '100%',
    aspectRatio: 1.85,
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  } as const,
  image: {
    width: '100%',
    height: '100%',
  } as const,
  cardContent: {
    padding: 12,
    flex: 1,
  } as const,
  textContainer: {
    gap: 4,
    marginBottom: 6,
  } as const,
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: '#F3F4F6',
    lineHeight: 18,
  } as const,
  description: {
    fontSize: 12,
    color: '#9CA3AF',
    lineHeight: 16,
  } as const,
  date: {
    fontSize: 11,
    color: '#9CA3AF',
  } as const,
});
